import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import {
  CreditCard, CheckCircle2, Clock, AlertTriangle, Loader2, ShieldCheck,
  TrendingUp, RefreshCw, Sparkles, Zap, Users as UsersIcon,
} from "lucide-react";
import { toast } from "sonner";
import { billingApi, creditsApi } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { PlanComparison } from "../components/billing/PlanComparison";
import BuyCreditsModal from "../components/BuyCreditsModal";
import { PromoBanner } from "../components/PromoBanner";
import ActivateUsersCard from "../components/ActivateUsersCard";

const STATUS_STYLES = {
  active: { label: "Active", cls: "bg-emerald-50 text-emerald-700 border-emerald-200", Icon: CheckCircle2 },
  trialing: { label: "Free trial", cls: "bg-amber-50 text-amber-700 border-amber-200", Icon: Clock },
  past_due: { label: "Past due", cls: "bg-rose-50 text-rose-700 border-rose-200", Icon: AlertTriangle },
  suspended: { label: "Suspended", cls: "bg-rose-50 text-rose-700 border-rose-200", Icon: AlertTriangle },
  canceled: { label: "Canceled", cls: "bg-slate-100 text-slate-600 border-slate-200", Icon: AlertTriangle },
};

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—");

export default function Billing() {
  const { refreshMe } = useAuth();
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creditBal, setCreditBal] = useState(null);
  const [creditModal, setCreditModal] = useState(false);
  const [creditLedger, setCreditLedger] = useState([]);
  const [params, setParams] = useSearchParams();
  // Bumping this forces the ActivateUsersCard + PlanComparison to refetch after
  // a successful checkout return without needing a full-page reload.
  const [refreshTick, setRefreshTick] = useState(0);

  const loadCredits = useCallback(async () => {
    try {
      const [bal, led] = await Promise.all([
        creditsApi.balance(),
        creditsApi.ledger(8).catch(() => []),
      ]);
      setCreditBal(bal);
      setCreditLedger(led || []);
      window.dispatchEvent(new Event("credits-updated"));
    } catch (_) { /* non-fatal */ }
  }, []);

  const load = useCallback(async () => {
    try {
      const s = await billingApi.state();
      setState(s);
    } catch (_) {
      toast.error("Could not load billing details.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); loadCredits(); }, [load, loadCredits]);

  // Return from a credit-pack Stripe checkout — poll for confirmation.
  useEffect(() => {
    const sessionId = params.get("credits_session");
    if (!sessionId) return;
    let attempts = 0, stop = false;
    const poll = async () => {
      if (stop) return;
      try {
        const res = await creditsApi.status(sessionId);
        if (res.payment_status === "paid") {
          toast.success("Payment received — credits added to your wallet!");
          setParams({}, { replace: true });
          await loadCredits();
          return;
        }
      } catch (_) { /* keep polling */ }
      attempts += 1;
      if (attempts >= 8) { toast.message("Still processing your purchase. Refresh in a moment."); setParams({}, { replace: true }); return; }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { stop = true; };
  }, [params, setParams, loadCredits]);

  // Return from a per-user activation Stripe checkout.
  useEffect(() => {
    const sessionId = params.get("activate_users_session");
    if (!sessionId) return;
    let attempts = 0, stop = false;
    const poll = async () => {
      if (stop) return;
      try {
        const res = await billingApi.status(sessionId);
        if (res.payment_status === "paid") {
          toast.success("Payment received — users activated!");
          setParams({}, { replace: true });
          await load();
          await refreshMe();
          setRefreshTick((n) => n + 1);
          return;
        }
      } catch (_) { /* keep polling */ }
      attempts += 1;
      if (attempts >= 8) { toast.message("Still processing your payment. Refresh in a moment."); setParams({}, { replace: true }); return; }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { stop = true; };
  }, [params, setParams, load, refreshMe]);

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center min-h-[60vh]">
        <Loader2 className="h-7 w-7 text-blue-500 animate-spin" />
      </div>
    );
  }
  if (!state) return null;

  const sty = STATUS_STYLES[state.subscriptionStatus] || STATUS_STYLES.active;
  const StatusIcon = sty.Icon;
  const seatPrice = state.seatPrice ?? 0;
  const autopay = state.autopay !== false;
  const per = state.term === "annual" ? "yr" : "mo";
  const isTrial = state.subscriptionStatus === "trialing";
  const activatedCount = state.activatedCount ?? 0;
  const monthlyEnd = state.monthlyPeriodEnd;
  const annualEnd = state.annualPeriodEnd;

  return (
    <div className="flex-1 overflow-y-auto" data-testid="billing-page">
      <div className="px-6 lg:px-8 py-6 max-w-4xl">
        <div className="flex items-center gap-2.5 mb-1">
          <CreditCard className="h-5 w-5 text-blue-600" />
          <h1 className="text-[22px] font-bold text-slate-900">Billing &amp; Subscription</h1>
        </div>
        <p className="text-[13.5px] text-slate-500 mb-6">
          Your plan renews automatically each billing cycle. Manage your card, download invoices, or cancel any time from the Stripe portal.
        </p>

        {/* Current subscription card */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden mb-6">
          <div className="px-6 py-5 border-b border-slate-100 flex items-center justify-between flex-wrap gap-3">
            <div>
              <div className="text-[12px] uppercase tracking-wider text-slate-400 font-semibold mb-1">Current plan</div>
              <div className="text-[20px] font-bold text-slate-900" data-testid="billing-plan-name">
                {state.planName || "No plan"}
                <span className="text-[13px] font-medium text-slate-400 ml-2">{state.term === "annual" ? "Annual" : "Monthly"} default</span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {autopay && (
                <span data-testid="billing-autopay-pill" className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full border text-[12px] font-semibold bg-blue-50 text-blue-700 border-blue-200">
                  <RefreshCw className="h-3.5 w-3.5" /> Auto-pay
                </span>
              )}
              <span data-testid="billing-status-pill" className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full border text-[12px] font-semibold ${sty.cls}`}>
                <StatusIcon className="h-3.5 w-3.5" /> {sty.label}
              </span>
              {state.stripeCustomerId && (
                <button
                  onClick={async () => {
                    try {
                      const res = await billingApi.portal(window.location.origin);
                      if (res?.url) { window.location.href = res.url; }
                    } catch (err) {
                      toast.error(err?.response?.data?.detail || "Could not open billing portal");
                    }
                  }}
                  data-testid="billing-manage-btn"
                  className="inline-flex items-center gap-1.5 px-3 h-8 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 text-[12.5px] font-semibold text-slate-700"
                  title="Update card, view invoices, or cancel"
                >
                  <CreditCard className="h-3.5 w-3.5" /> Manage billing
                </button>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 divide-x divide-y sm:divide-y-0 divide-slate-100">
            <Stat label="Plan price" value={`${money(seatPrice)}/${per}`} icon={CreditCard} />
            <Stat label="Activated users" value={`${activatedCount}`} sub={`${state.userCount ?? 0} total`} icon={UsersIcon} testId="billing-total-seats" />
            <Stat
              label="Monthly renewal"
              value={monthlyEnd ? fmtDate(monthlyEnd) : "—"}
              sub={monthlyEnd ? "Monthly users" : "No monthly users"}
              icon={ShieldCheck}
              testId="billing-monthly-renewal"
            />
            <Stat
              label="Annual renewal"
              value={annualEnd ? fmtDate(annualEnd) : "—"}
              sub={annualEnd ? "Annual users" : "No annual users"}
              icon={TrendingUp}
              testId="billing-annual-renewal"
            />
          </div>

          <div className="px-6 py-4 bg-slate-50/70 border-t border-slate-100 text-[12.5px] text-slate-500" data-testid="billing-period-note">
            {isTrial ? (
              <>Trial ends <strong className="text-slate-700">{fmtDate(state.trialEndsAt)}</strong>{state.daysLeft != null && ` · ${state.daysLeft} days left`} — activate users below to keep them past the trial.</>
            ) : activatedCount > 0 ? (
              <>You have <strong className="text-slate-700">{activatedCount}</strong> activated user{activatedCount === 1 ? "" : "s"}. Renewals bill on each term&apos;s anchor date.</>
            ) : (
              <>No active users yet — activate a teammate below to get started.</>
            )}
          </div>
        </div>

        {/* Per-user activation (Ship 1 — replaces the old team-seat control) */}
        <ActivateUsersCard key={refreshTick} />

        {/* AI Credits wallet */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden mb-6" data-testid="billing-credits-card">
          <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between gap-3">
            <div>
              <div className="text-[15px] font-bold text-slate-900 flex items-center gap-2"><Sparkles className="h-4 w-4 text-amber-500" /> AI Credits</div>
              <div className="text-[12.5px] text-slate-500 mt-0.5">Prepaid balance spent on all AI features (Guru, voice agent, automations). Purchased credits never expire; your monthly plan allowance refreshes each cycle.</div>
            </div>
            <button onClick={() => setCreditModal(true)} data-testid="billing-buy-credits-btn" className="inline-flex items-center gap-1.5 px-4 h-9 rounded-lg bg-amber-500 hover:bg-amber-600 text-amber-950 text-[13px] font-semibold shrink-0">
              <Zap className="h-4 w-4" /> Buy credits
            </button>
          </div>
          {creditBal?.promo && (
            <div className="px-6 pt-4">
              <PromoBanner promo={creditBal.promo} />
            </div>
          )}
          <div className="px-6 py-5">
            {creditBal ? (
              <>
                <div className="flex flex-wrap items-end gap-x-8 gap-y-3">
                  <div>
                    <div className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold">Available</div>
                    <div className="text-[30px] font-extrabold text-slate-900 tabular-nums leading-tight" data-testid="billing-credits-total">
                      {Number(creditBal.totalDisplay).toLocaleString()}
                      <span className="text-[14px] font-semibold text-slate-400 ml-1.5">credits</span>
                    </div>
                  </div>
                  <div className="text-[12.5px] text-slate-600 space-y-0.5">
                    <div>Monthly allowance: <strong>{Number(Math.floor(creditBal.monthly)).toLocaleString()}</strong> of {Number(creditBal.monthlyGrant).toLocaleString()}</div>
                    <div>Purchased (never expires): <strong>{Number(Math.floor(creditBal.purchased)).toLocaleString()}</strong></div>
                  </div>
                </div>
                {creditBal.empty ? (
                  <div className="mt-4 flex items-start gap-2 px-3 py-2.5 rounded-lg bg-rose-50 border border-rose-200 text-[12.5px] text-rose-800" data-testid="billing-credits-empty">
                    <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" /> You&apos;re out of AI credits — AI features are paused until you buy more.
                  </div>
                ) : creditBal.low ? (
                  <div className="mt-4 flex items-start gap-2 px-3 py-2.5 rounded-lg bg-amber-50 border border-amber-200 text-[12.5px] text-amber-800" data-testid="billing-credits-low">
                    <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" /> Running low — you have {Number(creditBal.totalDisplay).toLocaleString()} credits left (warns at {Number(creditBal.lowThreshold).toLocaleString()}).
                  </div>
                ) : null}
                {creditLedger.length > 0 && (
                  <div className="mt-5">
                    <div className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold mb-2">Recent activity</div>
                    <div className="divide-y divide-slate-100 rounded-lg border border-slate-100">
                      {creditLedger.map((l) => (
                        <div key={l.id} className="flex items-center justify-between px-3 py-2 text-[12.5px]" data-testid="credit-ledger-row">
                          <div className="flex items-center gap-2 text-slate-600">
                            <span className={`h-1.5 w-1.5 rounded-full ${l.credits >= 0 ? "bg-emerald-500" : "bg-slate-300"}`} />
                            <span className="capitalize">{(l.type || "").replace(/_/g, " ")}{l.feature ? ` · ${l.feature.replace(/_/g, " ")}` : ""}</span>
                          </div>
                          <div className="flex items-center gap-3">
                            <span className={`font-semibold tabular-nums ${l.credits >= 0 ? "text-emerald-600" : "text-slate-700"}`}>
                              {l.credits >= 0 ? "+" : ""}{Number(l.credits).toLocaleString(undefined, { maximumFractionDigits: 2 })}
                            </span>
                            <span className="text-slate-400">{fmtDate(l.createdAt)}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="py-6 text-center text-slate-400 text-[13px]"><Loader2 className="h-4 w-4 animate-spin inline mr-2" /> Loading credits…</div>
            )}
          </div>
        </div>
        <BuyCreditsModal open={creditModal} onClose={() => { setCreditModal(false); loadCredits(); }} />

        {/* Scheduled plan/term change banner */}
        {(state.pendingPlanName || (state.pendingTerm && state.pendingTerm !== state.term)) && (
          <div className="flex items-start gap-2.5 px-4 py-3 rounded-lg bg-indigo-50 border border-indigo-200 text-indigo-800 text-[13px] mb-6" data-testid="billing-scheduled-banner">
            <Clock className="h-4 w-4 mt-0.5 shrink-0 text-indigo-500" />
            <span className="flex-1">
              Scheduled for your next renewal{state.currentPeriodEnd ? ` (${fmtDate(state.currentPeriodEnd)})` : ""}:
              {state.pendingPlanName && <strong> switch to {state.pendingPlanName}</strong>}
              {state.pendingPlanName && state.pendingTerm && state.pendingTerm !== state.term && " ·"}
              {state.pendingTerm && state.pendingTerm !== state.term && <strong> {state.pendingTerm} billing</strong>}.
            </span>
            <button onClick={async () => { await billingApi.cancelScheduledChange(); toast.success("Scheduled change cancelled."); load(); }} data-testid="billing-cancel-scheduled" className="text-[12.5px] font-semibold text-indigo-700 hover:text-indigo-900 underline shrink-0">
              Cancel
            </button>
          </div>
        )}

        {/* Plans & pricing (mirrors Platform-configured plans) */}
        {state.planName && <PlanComparison current={state} onChanged={load} />}
      </div>
    </div>
  );
}

function Stat({ label, value, icon: Icon, warn, sub, accentSub, testId }) {
  return (
    <div className="px-6 py-5" data-testid={testId}>
      <div className="flex items-center gap-1.5 text-[11.5px] uppercase tracking-wider text-slate-400 font-semibold mb-1.5">
        <Icon className="h-3.5 w-3.5" /> {label}
      </div>
      <div className={`text-[19px] font-bold ${warn ? "text-amber-600" : "text-slate-900"}`}>{value}</div>
      {sub && <div className={`text-[11.5px] font-medium mt-0.5 ${accentSub || "text-slate-400"}`}>{sub}</div>}
    </div>
  );
}
